
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Terminal, ShieldAlert, Cpu } from 'lucide-react';
import { motion } from 'framer-motion';
import * as Sentry from "@sentry/react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    Sentry.captureException(error, { extra: { errorInfo } });
    (this as any).setState({ errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-sal-gunmetal flex flex-col items-center justify-center p-6 text-white relative overflow-hidden font-sans">
          {/* Cockpit Error Background */}
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20 pointer-events-none"></div>
          <div className="absolute top-0 left-0 w-full h-2 bg-rose-600 shadow-[0_0_30px_rgba(225,29,72,0.8)] z-50"></div>

          <div className="fixed inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-rose-900/10 rounded-full blur-[120px] animate-pulse"></div>
          </div>

          <div className="relative z-10 w-full max-w-xl bg-sal-gunmetal-light/80 backdrop-blur-2xl border border-rose-500/20 rounded-[2.5rem] p-10 shadow-[0_40px_80px_rgba(0,0,0,0.6)]">
            <div className="flex flex-col items-center text-center mb-8">
              <div className="p-5 bg-rose-600 rounded-3xl border border-rose-400/50 shadow-[0_0_40px_rgba(225,29,72,0.4)] mb-6 animate-pulse">
                <ShieldAlert className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-3xl font-black tracking-tighter text-white uppercase leading-none mb-2">Colapso no Sistema</h1>
              <p className="text-xs text-rose-400 font-black tracking-[0.3em] uppercase">Protocolo de Segurança Crítico Ativo</p>
            </div>

            <div className="bg-black/60 rounded-3xl p-6 font-mono text-xs text-rose-200/80 mb-8 border-l-4 border-rose-600 shadow-inner overflow-hidden relative">
              <div className="absolute top-0 right-0 p-4 opacity-10"><Cpu className="w-20 h-20" /></div>
              <div className="flex items-center gap-2 mb-3 text-rose-500 font-black uppercase tracking-widest text-xs">
                <Terminal className="w-3.5 h-3.5" /> Diagnóstico do Core
              </div>
              <p className="mb-4 font-black text-white text-sm">{this.state.error?.toString()}</p>
              <div className="max-h-40 overflow-y-auto no-scrollbar opacity-60 italic leading-relaxed">
                {this.state.errorInfo?.componentStack}
              </div>
            </div>

            <button
              onClick={this.handleReload}
              className="w-full bg-rose-600 hover:bg-rose-500 text-white font-black py-5 rounded-2xl shadow-xl shadow-rose-900/40 transition-all active:scale-[0.98] flex items-center justify-center gap-3 uppercase tracking-[0.2em] text-base"
            >
              <RefreshCw className="w-5 h-5" /> Reinicializar S.A.L.
            </button>

            <p className="text-center text-xs font-black text-slate-600 mt-8 uppercase tracking-[0.4em] opacity-40">
              S.A.L. ROTA v3.4 • DEEP FOCUS SYSTEM • EMERGENCY MODE
            </p>
          </div>
        </div>
      );
    }

    return (this as any).props.children;
  }
}
